import React, { useState } from 'react';
import { Box, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import useUserAuth from '../../../utils/useUserAuth';
import { toggleEmployeeExposure } from '../../../api/exposure';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonSelect from '../../../form-components/CommonSelect';
import ConfirmModal from '../../common/modal/ConfirmModal';
import { pageSize } from '../../../constants/constants';
import {
  TableCard,
  TableHeaderRow,
  TableTitle,
  TableTitleSection,
  FilterSection,
  SearchField,
  SearchInput,
  ToggleLabel,
  TealSwitch,
  StyledHeaderCell,
  StyledBodyRow,
  StyledBodyCell,
  SalaryChip,
  StatusChip,
  EmptyMessage,
  GuideButton,
} from './EmployeeSettingStyled';

const RESPONSIBILITY_LABELS = {
  PRIVATE_LESSON: '개인레슨',
  GROUP_LESSON: '그룹레슨',
  ONLINE_LESSON: '온라인레슨',
  CUSTOMER_MANAGEMENT: '고객관리',
  STORE_MANAGEMENT: '매장관리',
};

// 급여유형 하드코딩 매핑 (백엔드 API 확장 전까지 임시)
const SALARY_TYPE_LABELS = {
  COUNT: '횟수제',
  COUNTING: '횟수제',
  MONTHLY: '월급제',
  HOURLY: '시간제',
};

const TABLE_COLUMNS = [
  { key: 'hiredDate', label: '입사일', width: 140, sortable: true },
  { key: 'name', label: '임직원', width: 80, sortable: true },
  { key: 'salaryType', label: '급여유형', width: 120 },
  { key: 'position', label: '직급', width: 140, sortable: true },
  { key: 'gender', label: '성별', width: 100, sortable: true },
  { key: 'phoneNumber', label: '전화번호', width: 200 },
  { key: 'responsibilities', label: '담당업무', width: 320 },
  { key: 'accessZones', label: '출입가능 구역', width: undefined },
  { key: 'classification', label: '재직구분', width: 100, sortable: true },
  { key: 'exposure', label: '앱노출 여부', width: 100 },
];

const EmployeeSettingTable = ({
  employees,
  query,
  onSearch,
  onPageChange,
  onLimitChange,
  loading,
  excludeLeft,
  onExcludeLeftChange,
  positionFilter,
  onPositionFilterChange,
  positionOptions,
  classificationFilter,
  onClassificationFilterChange,
  salaryTypeFilter,
  onSalaryTypeFilterChange,
  onExcelDownload,
  onRowClick,
  sortField,
  sortDirection,
  onSortChange,
  onGuideOpen,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);
  const canView = useUserAuth('EMPLOYEE_VIEW');
  const [, forceUpdate] = useState(0);
  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);
  const [exposureAlertMessage, setExposureAlertMessage] = useState('');
  const [searchValue, setSearchValue] = useState(query.searchInput || '');

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      onSearch(searchValue);
    }
  };

  const handleExposureToggle = async (e, employee) => {
    e.preventDefault();
    e.stopPropagation();
    const isWorking = employee.classification === 'WORKING';
    const isDefaultAdmin = employee.isDefaultAdmin === 'Y';

    if (!isWorking) {
      setExposureAlertMessage('퇴사한 직원은\n앱 노출이 불가합니다.');
      setExposureAlertOpen(true);
      return;
    }
    if (isDefaultAdmin) {
      setExposureAlertMessage('기본 관리자는\n앱 노출을 변경할 수 없습니다.');
      setExposureAlertOpen(true);
      return;
    }
    const newValue = employee.exposureYn === 'Y' ? 'N' : 'Y';
    try {
      await toggleEmployeeExposure(user.token, employee.id, newValue, user.branch?.branchId);
      employee.exposureYn = newValue;
      forceUpdate(n => n + 1);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨겨집니다');
    } catch {
      toast.error('변경에 실패했습니다');
    }
  };

  // 급여유형 하드코딩 결정 로직
  const getSalaryTypeLabel = employee => {
    if (employee.salaryType) {
      return SALARY_TYPE_LABELS[employee.salaryType] || employee.salaryType;
    }
    // 백엔드에 salaryType이 없으므로 하드코딩: 횟수제로 기본 표시
    return '횟수제';
  };

  return (
    <TableCard>
      {/* Header: 제목 + 검색 + 필터 */}
      <TableHeaderRow>
        <TableTitleSection>
          <TableTitle>임직원 목록</TableTitle>
          <SearchField>
            <SearchInput
              placeholder="이름 및 연락처로 검색"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <SearchIcon
              sx={{ color: '#a6a6a6', cursor: 'pointer', fontSize: 24 }}
              onClick={() => onSearch(searchValue)}
            />
          </SearchField>
          <TealSwitch
            checked={excludeLeft}
            onChange={e => onExcludeLeftChange(e.target.checked)}
          />
          <ToggleLabel>퇴사자 제외</ToggleLabel>
        </TableTitleSection>

        <FilterSection>
          <GuideButton onClick={onGuideOpen}>📖 임직원 설정 가이드</GuideButton>

          <CommonSelect
            value={positionFilter}
            onChange={e => onPositionFilterChange(e.target.value)}
            emptyLabel="전체 직급"
            width={130}
            options={positionOptions}
          />

          {/* <CommonSelect
            value={classificationFilter}
            onChange={e => onClassificationFilterChange(e.target.value)}
            emptyLabel="전체 재직구분"
            width={150}
            options={[
              { value: 'WORKING', label: '재직중' },
              { value: 'LEFT', label: '퇴사' },
            ]}
          /> */}

          {/* <CommonSelect
            value={salaryTypeFilter}
            onChange={e => onSalaryTypeFilterChange(e.target.value)}
            emptyLabel="전체 급여유형"
            width={150}
            options={[
              { value: 'COUNTING', label: '횟수제' },
              { value: 'MONTHLY', label: '월급제' },
              { value: 'HOURLY', label: '시급제' },
              { value: 'DAILY', label: '일급제' },
            ]}
          /> */}

          <CommonExcelButton onClick={onExcelDownload}>
            전체 엑셀 다운로드
          </CommonExcelButton>

          <CommonSelect
            value={query.limit}
            onChange={onLimitChange}
            width={106}
            options={pageSize}
          />
        </FilterSection>
      </TableHeaderRow>

      {/* Table */}
      <Box sx={{ position: 'relative', marginTop: '16px' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2,
            backgroundColor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px',
          }}>
            <Box sx={{
              width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTop: '3px solid #4ab3bc',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
            }} />
          </Box>
        )}
      <TableContainer sx={{ border: '1px solid #f2f2f2', borderRadius: '8px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f4f6f9' }}>
            <TableRow sx={{ height: '40px' }}>
              {TABLE_COLUMNS.map(col => {
                const isActive = sortField === col.key;
                const isAsc = isActive && sortDirection === 'ASC';
                const isDesc = isActive && sortDirection === 'DESC';

                const handleSortClick = () => {
                  if (!col.sortable) return;
                  if (!isActive) {
                    onSortChange(col.key, 'DESC');
                  } else if (isDesc) {
                    onSortChange(col.key, 'ASC');
                  } else if (isAsc) {
                    onSortChange('', '');
                  }
                };

                const sortIcon = col.sortable && (
                  isAsc ? <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4ab3bc' }} />
                  : isDesc ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4ab3bc' }} />
                  : <UnfoldMoreIcon sx={{ fontSize: 16, color: '#d4d4d4' }} />
                );

                return (
                  <StyledHeaderCell
                    key={col.key}
                    sx={{
                      width: col.width,
                      minWidth: col.width,
                      cursor: col.sortable ? 'pointer' : 'default',
                      userSelect: col.sortable ? 'none' : 'auto',
                      '&:hover': col.sortable ? { backgroundColor: '#ebeef2' } : {},
                      transition: 'background 0.1s',
                    }}
                    onClick={handleSortClick}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                      {col.key === 'position' && (
                        <Tooltip title="직급관리의 직급 순서대로 정렬됩니다" placement="top" arrow componentsProps={{ tooltip: { sx: { fontSize: '12px' } } }}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: '#bbb', '&:hover': { color: '#4AB3BC' } }} />
                        </Tooltip>
                      )}
                      {col.label}
                      {sortIcon}
                    </Box>
                  </StyledHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {employees?.content?.length > 0 ? (
              employees.content.map((employee, index) => {
                const isWorking = employee.classification === 'WORKING';
                const isDefaultAdmin = employee.isDefaultAdmin === 'Y';
                const canClick = canView && !isDefaultAdmin;
                const salaryLabel = getSalaryTypeLabel(employee);

                return (
                  <Tooltip
                    key={index}
                    title={isDefaultAdmin ? '기본 관리자는 수정할 수 없습니다' : ''}
                    arrow
                    followCursor
                    componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}
                  >
                    <StyledBodyRow
                      clickable={canClick}
                      retired={!isWorking}
                      {...(canClick ? { onClick: () => { onRowClick?.(); navigate(`/employee/${employee.id}`); } } : {})}
                    >
                      <StyledBodyCell>{employee.hiredDate || '-'}</StyledBodyCell>
                      <StyledBodyCell sx={{ fontWeight: 500, color: '#262626' }}>{employee.name}</StyledBodyCell>
                      <StyledBodyCell>
                        {employee.salaryType
                          ? <SalaryChip salaryType={getSalaryTypeLabel(employee)}>{getSalaryTypeLabel(employee)}</SalaryChip>
                          : <span style={{ color: '#bbb' }}>-</span>
                        }
                      </StyledBodyCell>
                      <StyledBodyCell>{employee.empoloyeePositionName || '-'}</StyledBodyCell>
                      <StyledBodyCell>{employee.gender === 'FEMALE' ? '여성' : '남성'}</StyledBodyCell>
                      <StyledBodyCell>{employee.phoneNumber}</StyledBodyCell>
                      <StyledBodyCell>
                        {employee.responsibilities?.length > 0
                          ? employee.responsibilities.map(r => RESPONSIBILITY_LABELS[r] || '').filter(Boolean).join(', ')
                          : '-'}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {employee.accessGroupNames?.length > 0
                          ? employee.accessGroupNames.join(', ')
                          : '-'}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        <StatusChip isWorking={isWorking}>{isWorking ? '재직중' : '퇴사'}</StatusChip>
                      </StyledBodyCell>
                      <StyledBodyCell>
                        <div onClick={e => e.stopPropagation()}>
                          <TealSwitch
                            checked={employee.exposureYn === 'Y'}
                            onChange={e => handleExposureToggle(e, employee)}
                          />
                        </div>
                      </StyledBodyCell>
                    </StyledBodyRow>
                  </Tooltip>
                );
              })
            ) : (
              <TableRow>
                <StyledBodyCell colSpan={TABLE_COLUMNS.length}>
                  <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
                </StyledBodyCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Pagination */}
      <CommonPagination
        totalElements={employees?.totalElements}
        limit={query.limit}
        page={query.page}
        onChange={onPageChange}
      />

      {/* Alert Modal */}
      <ConfirmModal
        open={exposureAlertOpen}
        onClose={() => setExposureAlertOpen(false)}
        onConfirm={() => setExposureAlertOpen(false)}
        message={exposureAlertMessage}
        highlightText="__none__"
        highlightColor="#4AB3BC"
        cancelText="닫기"
        confirmText="확인"
        confirmColor="#4AB3BC"
      />
    </TableCard>
  );
};

export default EmployeeSettingTable;
